import React, { MouseEvent, useEffect, useMemo, useRef } from "react"
import { Search, SlidersHorizontal } from "lucide-react"
import type {
  DetailTab,
  PaginationMeta,
  StudentQuestionListItem,
} from "../types"
import { mapQuestionCard } from "../utils/mapQuestionCard"
import QuestionCard from "./QuestionCard"
import EmptyState from "./EmptyState"
import "./QuestionList.style.scss"

interface QuestionListProps {
  items: StudentQuestionListItem[]
  loading: boolean
  error: string
  pagination: PaginationMeta | null
  selectedId: string | null
  keyword: string
  hiddenOnMobile?: boolean
  onKeywordChange: (value: string) => void
  onToggleFilters: () => void
  onOpen: (id: string, tab?: DetailTab) => void
  onToggleBookmark: (
    e: MouseEvent,
    id: string,
    currentlyBookmarked: boolean
  ) => void
  onPageChange: (page: number) => void
}

const QuestionList = ({
  items,
  loading,
  error,
  pagination,
  selectedId,
  keyword,
  hiddenOnMobile,
  onKeywordChange,
  onToggleFilters,
  onOpen,
  onToggleBookmark,
  onPageChange,
}: QuestionListProps) => {
  const cards = useMemo(() => items.map(mapQuestionCard), [items])
  const total = pagination?.total ?? 0
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!selectedId) return
    bodyRef.current?.scrollTo({ top: 0 })
  }, [selectedId])

  return (
    <section
      className={`question-list ${hiddenOnMobile ? "is-hidden-mobile" : ""}`}
    >
      <div className="question-list__toolbar">
        <div className="question-list__heading">
          <div>
            <h1>Question Library</h1>
            <p>
              {total > 0
                ? `Browse ${total.toLocaleString()}${total >= 100 ? "+" : ""} past paper & practice questions.`
                : "Browse past paper & practice questions."}
            </p>
          </div>
          <button
            type="button"
            className="question-list__filters-btn"
            onClick={onToggleFilters}
          >
            <SlidersHorizontal size={16} />
            <span>Filters</span>
          </button>
        </div>

        <div className="question-list__search">
          <Search size={16} />
          <input
            type="search"
            placeholder="Search by keyword, year, or topic..."
            value={keyword}
            onChange={(e) => onKeywordChange(e.target.value)}
            aria-label="Search questions"
          />
        </div>
      </div>

      <div className="question-list__body" ref={bodyRef}>
        <div className="question-list__stack">
          {loading && <EmptyState title="Loading questions…" />}
          {!loading && error && (
            <EmptyState title="Couldn’t load questions" description={error} />
          )}
          {!loading && !error && cards.length === 0 && (
            <EmptyState
              title="No questions found"
              description="Try adjusting filters or search keywords."
            />
          )}
          {!loading &&
            !error &&
            cards.map((card) => (
              <QuestionCard
                key={card.id}
                card={card}
                selected={selectedId === card.id}
                isCompleted={selectedId === card.id}
                onOpen={(tab) => onOpen(card.id, tab)}
                onToggleBookmark={(e) =>
                  onToggleBookmark(e, card.id, card.isBookmarked)
                }
              />
            ))}
        </div>
      </div>

      {pagination && pagination.total_pages > 0 && (
        <div className="question-list__pagination">
          <button
            type="button"
            disabled={pagination.page <= 1 || loading}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {Math.max(pagination.total_pages, 1)}
            {total > 0 ? ` · ${total} total` : ""}
          </span>
          <button
            type="button"
            disabled={
              pagination.page >= pagination.total_pages ||
              pagination.total_pages <= 1 ||
              loading
            }
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  )
}

export default QuestionList
